<template lang="pug">
control
  field(:class="{ invalid: !currency.meta.valid && currency.meta.touched }")
    label(:for="currency.id")(v-html="currency.label")
    input(
      @blur="currency.blur",
      @keydown.up.prevent="currency.increase",
      @keydown.down.prevent="currency.decrease",
      v-model="currency.model",
      :type="currency.type",
      :name="currency.id",
      :id="currency.id",
      :placeholder="currency.placeholder()",
    )
    error(v-if="!currency.meta.valid && currency.meta.touched") {{ currency.errorMessage }}
</template>

<script>
import { useStore } from "vuex";
import { computed, ref, watch, reactive } from "vue";
import { useField } from "vee-validate";
import { required } from "../composables/required.js";

import * as yup from "yup";

export default {
  name: "Currency",
  props: {
    field: Object,
  },
  setup(p) {
    const store = useStore();
    const currency = reactive(initCurrency());

    return { currency };

    function initCurrency() {
      const field = computed(() => p.field);
      const id = field.value.id;
      const type = "text";

      let rulesBase = yup.number().nullable().integer();

      const conditionalRules = (rules, field, isRequired) => {
        if (isRequired.value) {
          rules.value = rules.value.min(
            field.value.min ? field.value.min : isRequired.value ? 1 : 0
          );
        }
        return rules.value;
      };

      const { rules, label, isRequired } = required(
        field,
        rulesBase,
        conditionalRules
      );

      const { value, errorMessage, meta, handleBlur, validate } = useField(
        id,
        rules,
        {
          validateOnInput: true,
          initialValue: store.getters.model[id],
        }
      );

      const mask = {
        add(v) {
          v = this.remove(v);
          if (!v) return "";
          const formatter = new Intl.NumberFormat("en-US", {
            style: "currency",
            currency: "USD",
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
          });
          let model = parseInt(v);

          if (model) {
            model = formatter.format(String(model));
          }
          return model;
        },
        remove(v) {
          v = String(v);
          v = v.replaceAll(/\D/gim, "");
          v = parseInt(v);
          if (!v) v = 0;
          return v;
        },
      };

      const model = ref(0 /*fixed*/);
      const initial = ref(true);

      watch(
        () => mask.add(store.getters.model[id]),
        (v) => (model.value = mask.add(v))
      );

      watch(model, (v) => {
        if (Object.hasOwn(store.getters.model, id) || initial.value) {
          let masked = mask.add(v);
          let unmasked = mask.remove(v);
          if (!masked) {
            model.value = "";
            if (field.value.required !== false) value.value = 0;
            else value.value = null;
          } else {
            if (model.value !== masked) {
              model.value = masked;
            }
            value.value = unmasked;
          }
          initial.value = false;
          store.commit("updateModel", { id, value: unmasked });
        }
      });

      model.value = mask.add(store.getters.model[id]) || "";

      const increase = () => {
        let value = mask.remove(model.value);
        model.value = ++value;
      };

      const decrease = () => {
        let value = mask.remove(model.value) || 0;
        if (value >= 1) {
          model.value = --value;
        }
      };

      const blur = async () => {
        store.commit("pushLastFieldToDataLayer", id);
        await validate();
        handleBlur();
      };

      const placeholder = () => {
        return isRequired.value ? "(Required)" : "";
      };

      return {
        id,
        label,
        type,
        model,
        meta,
        blur,
        increase,
        decrease,
        errorMessage,
        placeholder,
      };
    }
  },
};
</script>

<style lang="scss" scoped></style>
